package com.neurobs.ccabserver.administrator.controller

import com.neurobs.errors.{Result, toResult, tryResult}
import com.neurobs.js.controller.BasicAdminPanelController
import com.neurobs.js.dom.ElementFactory.{defaultGridSpacingEm, makeButton, makeTextInput}
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.{ElementFactory, Grid, InputTextBox, RecordTableView}
import com.neurobs.js.util.StringUtilities.{dateTimeString, localToDateTime}
import com.neurobs.util.SecondsTimestamp

import org.scalajs.dom.raw.HTMLElement
import scalatags.JsDom.all._
import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global
import scala.collection.mutable
import scala.scalajs.js.Date

class TableNavigationPanelController[SearchData, T](title: String, adapter: TableNavigationController.Adapter[SearchData, T]) extends BasicAdminPanelController(title = title, showRefresh = true, showClose = true) {
	import com.neurobs.js.dom.Grid._

	private val tableNavigator = new TableNavigationController(adapter, setError)
	grid.addRow(tableNavigator.container)

	override def refresh(): Unit = {
		tableNavigator.refresh()
	}
}

object TableNavigationController {

	case class NavigationSearchData[SearchData]
	(
		minTime: Option[SecondsTimestamp] = None,
		maxTime: Option[SecondsTimestamp] = None,
		maxId: Option[Long] = None,
		limit: Int = 20,
		data: SearchData
	)

	abstract class SearchForm[SearchData] {
		def container: HTMLElement

		def set(s: SearchData): Unit

		def get: Result[SearchData]

		def clear(): Unit
	}

	abstract class Adapter[SearchData, T] {
		def getId(d: T): Long

		def get(search: NavigationSearchData[SearchData]): Future[Result[Seq[T]]]

		def columnHeaders: Seq[RecordTableView.ColumnHeader]

		def recordRow(d: T): Seq[RecordTableView.CellContent]

		def createSearchForm: SearchForm[SearchData]
	}

	class SearchFormView[SearchData](custom: SearchForm[SearchData]) {
		private val minTimeBox = InputTextBox(makeTextInput(width = 15))
		private val maxTimeBox = InputTextBox(makeTextInput(width = 15))
		private val maxIdBox = InputTextBox(makeTextInput(width = 10))

		val container: HTMLElement = {
			val temp = ElementFactory.makeFormTable(Array(
				("Min Time:", minTimeBox.text),
				("Max Time:", maxTimeBox.text),
				("Max Id:", maxIdBox.text)
			))
			temp.style.margin = "0"
			div(custom.container, temp).render
		}

		/** Sets the input elements to match the given data
		 *
		 * @param data The new data
		 */
		def set(data: NavigationSearchData[SearchData]): Unit = {
			minTimeBox.setValue(data.minTime.fold("")(dateTimeString))
			maxTimeBox.setValue(data.maxTime.fold("")(dateTimeString))
			maxIdBox.setValue(data.maxId.fold("")(_.toString))
			custom.set(data.data)
		}

		/** Gets the parameters from the input elements
		 *
		 * @return Error if any elements are invalid, or value
		 */
		def get: Result[NavigationSearchData[SearchData]] = {
			def getDateTime(b: InputTextBox): Result[Option[SecondsTimestamp]] = {
				val s = b.value()
				if (s.isEmpty) Right(None)
				else toResult(localToDateTime(s), noValueMessage = s"Invalid date time string '$s'.").map(Some(_))
			}
			for {
				minTime <- getDateTime(minTimeBox)
				maxTime <- getDateTime(maxTimeBox)
				maxId <- tryResult(if (maxIdBox.value().isEmpty) None else Some(maxIdBox.value().toLong))
				customData <- custom.get
			} yield NavigationSearchData[SearchData](
				minTime = minTime,
				maxTime = maxTime,
				maxId = maxId,
				data = customData
			)
		}

		/** Sets the min and max times to today (local time) */
		def setToToday(): Unit = {
			val today = new Date()
			today.setTime(Date.now())
			val day = today.getDate()
			val month = today.getMonth() + 1
			val year = today.getFullYear()
			minTimeBox.setValue(s"$month/$day/$year 00:00:00")
			maxTimeBox.setValue(s"$month/$day/$year 23:59:59")
		}

		/** Sets all boxes to empty  */
		def clear(): Unit = {
			minTimeBox.setValue("")
			maxTimeBox.setValue("")
			maxIdBox.setValue("")
			custom.clear()
		}
	}

	/** Maintains information about the last search displayed and
	 * whether it represents partial results that have previous
	 * and next screens of results
	 */
	class ResultSet[SearchData, T](adapter: Adapter[SearchData, T]) {
		case class SearchRun(parameters: NavigationSearchData[SearchData], minId: Long, hasPrevious: Boolean) {
			def previous: NavigationSearchData[SearchData] = parameters.copy(maxId = Some(minId - 1))
		}
		private val set = new mutable.ArrayBuffer[SearchRun]

		def hasNext: Boolean = set.size > 1
		def hasPrevious: Boolean = set.nonEmpty && set.last.hasPrevious

		def next: Option[NavigationSearchData[SearchData]] = if (hasNext) Some(set(set.size - 2).parameters) else None
		def previous: Option[NavigationSearchData[SearchData]] = if (hasPrevious) Some(set.last.previous) else None

		def add(current: NavigationSearchData[SearchData], data: Seq[T]): Unit = {
			val run = {
				if (data.isEmpty) SearchRun(current, minId = 0, hasPrevious = false)
				else {
					val minId = adapter.getId(data.minBy(adapter.getId))
					val hasPrevious = data.size == current.limit && minId > 1
					SearchRun(current, minId, hasPrevious)
				}
			}
			if (next.contains(current)) {
				set.reduceToSize(set.size - 2)
			}
			else if (!previous.contains(current)) {
				set.clear()
			}
			set += run
		}
	}
}

class TableNavigationController[SearchData, T](adapter: TableNavigationController.Adapter[SearchData, T], setError: String => Unit) {
	import TableNavigationController._

	private val table = new RecordTableView(adapter.columnHeaders)
	private val searchForm = new SearchFormView(adapter.createSearchForm)
	private val results = new ResultSet(adapter)

	private val nextButton = makeButton(caption = "Next").render
	private val previousButton = makeButton(caption = "Previous").render

	/** Searches and displays results for the given search
	 *
	 * @param searchParameters Parameter to search with, and to set in
	 *                         the GUI form
	 */
	def search(searchParameters: NavigationSearchData[SearchData]): Unit = {
		searchForm.set(searchParameters)
		setError("")

		adapter.get(searchParameters) map {
			case Left(error) => setError(error.description)
			case Right(data) =>
				table.setContents(data, adapter.recordRow)
				results.add(searchParameters, data)
				nextButton.style.display = if (results.hasNext) "inline-block" else "none"
				previousButton.style.display = if (results.hasPrevious) "inline-block" else "none"
		}
	}

	val container: HTMLElement = {
		import Grid._

		val grid: Grid = new Grid(fullWidth = true, defaultGridSpacingEm)

		val todayButton = makeButton(caption = "Today").render
		todayButton.onclick = _ => {
			searchForm.setToToday()
			refresh()
		}
		val clearButton = makeButton(caption = "Clear").render
		clearButton.onclick = _ => {
			searchForm.clear()
			refresh()
		}
		val searchButton = makeButton(caption = "Search").render
		searchButton.onclick = _ => refresh()

		nextButton.onclick = _ => results.next.foreach(search(_))
		previousButton.onclick = _ => results.previous.foreach(search(_))

		grid.addRow(table.container)
		grid.addRow(div(previousButton, " ", nextButton).render)
		grid.addRow(searchForm.container)
		grid.addRow(div(searchButton, " ", todayButton, " ", clearButton).render)

		grid.container
	}

	def refresh(): Unit = {
		searchForm.get match {
			case Left(error) => setError(error.description)
			case Right(searchParameters) => search(searchParameters)
		}
	}
}
